import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTenant } from '@core/tenant';
import type { Reservation } from '../hooks/useUserReservations';
import { userReservationsKeys } from '../hooks/useUserReservations';
import {
  checkModifyTimePolicy,
  cancelReservation,
  markWontAttend,
} from '../api/reservationApi';

type Step = 'loading' | 'confirm' | 'cantCancel' | 'wontAttendDone';

interface CancelReservationViewProps {
  reservation: Reservation;
  onClose: () => void;
  onSuccess: () => void;
}

const CancelReservationView = ({ reservation, onClose, onSuccess }: CancelReservationViewProps) => {
  const { t, i18n } = useTranslation('profile');
  const lang = i18n.language || 'es';
  const { restaurant } = useTenant();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('loading');
  const [policyHours, setPolicyHours] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const skipPolicyCheck = reservation.inWaitingList || reservation.inRequestBookintList;
  const hasGuarantee = reservation.type !== undefined && reservation.type !== 'traditional';

  useEffect(() => {
    if (skipPolicyCheck) {
      setStep('confirm');
      return;
    }

    let cancelled = false;

    async function init() {
      try {
        const policy = await checkModifyTimePolicy(reservation.id);
        if (cancelled) return;

        if (!policy.canModify) {
          setPolicyHours(policy.cancellationPolicyHours);
          setStep('cantCancel');
        } else {
          setStep('confirm');
        }
      } catch {
        if (!cancelled) {
          setError(t('MODIFY_RESERVATION_STRING_26_ERROR'));
          setStep('confirm');
        }
      }
    }

    init();
    return () => { cancelled = true; };
  }, [reservation.id, skipPolicyCheck, t]);

  const handleCancel = async () => {
    setSubmitting(true);
    setError(null);

    try {
      await cancelReservation(lang, reservation.id);

      if (restaurant?.id) {
        await queryClient.invalidateQueries({
          queryKey: userReservationsKeys.byRestaurant(restaurant.id),
        });
      }

      onSuccess();
    } catch {
      setSubmitting(false);
      setError(t('MODIFY_RESERVATION_STRING_26_ERROR'));
    }
  };

  const handleWontAttend = async () => {
    setSubmitting(true);
    setError(null);

    try {
      await markWontAttend(reservation.id);
      setSubmitting(false);
      setStep('wontAttendDone');
    } catch {
      setSubmitting(false);
      setError(t('MODIFY_RESERVATION_STRING_26_ERROR'));
    }
  };

  if (step === 'loading') {
    return (
      <div className="w-full flex flex-col items-center py-12">
        <Loader2 size={24} className="animate-spin text-brand" />
      </div>
    );
  }

  if (step === 'wontAttendDone') {
    return (
      <div className="w-full flex flex-col items-center">
        <h2 className="text-3xl font-display mb-4">{t('MODIFY_RESERVATION_STRING_37')}</h2>
        <p className="text-sm text-foreground/70 text-center mb-8 max-w-xs">
          {t('MODIFY_RESERVATION_STRING_38')}
        </p>

        {restaurant?.contactEmail && (
          <p className="text-sm text-foreground/50 mb-6">{restaurant.contactEmail}</p>
        )}

        <div className="w-full max-w-xs">
          <button
            className="w-full py-3 rounded-full border border-border text-brand-dark font-medium text-sm tracking-wide hover:bg-foreground/5 transition-colors"
            onClick={onClose}
          >
            {t('MODIFY_RESERVATION_STRING_26_CLOSE')}
          </button>
        </div>
      </div>
    );
  }

  if (step === 'cantCancel') {
    return (
      <div className="w-full flex flex-col items-center">
        <h2 className="text-3xl font-display mb-4">{t('MODIFY_RESERVATION_STRING_28_TITLE')}</h2>
        {/* Only a positive hour count is a window; -1, 0 or a missing value means no cancellations at all. */}
        {policyHours > 0 ? (
          <>
            <p className="text-sm text-foreground/70 text-center mb-2 max-w-xs">
              {t('MODIFY_RESERVATION_STRING_28', { hours: policyHours })}
            </p>
            <p className="text-lg font-semibold text-red-500 mb-6">
              {t('MODIFY_RESERVATION_STRING_35')}
            </p>
          </>
        ) : (
          <p className="text-sm text-foreground/70 text-center mb-6 max-w-xs">
            {t('MODIFY_RESERVATION_STRING_28_CLOSED')}
          </p>
        )}

        <p className="text-xs text-foreground/50 text-center mb-8 max-w-xs">
          {hasGuarantee
            ? t('MODIFY_RESERVATION_STRING_39')
            : t('MODIFY_RESERVATION_STRING_26_CONSIDER')}
        </p>

        {restaurant?.contactEmail && (
          <p className="text-sm text-foreground/50 mb-6">{restaurant.contactEmail}</p>
        )}

        {error && (
          <p className="text-red-500 text-sm text-center mb-4">{error}</p>
        )}

        <div className="w-full max-w-xs space-y-3">
          <button
            className="w-full py-3 rounded-full bg-brand-dark text-brand-foreground font-medium text-sm tracking-wide hover:bg-brand transition-colors"
            onClick={onClose}
          >
            {t('MODIFY_RESERVATION_STRING_24')}
          </button>
          <button
            className="w-full py-3 rounded-full border border-red-300 text-red-500 font-medium text-sm tracking-wide hover:bg-red-50 transition-colors disabled:opacity-50"
            onClick={handleWontAttend}
            disabled={submitting}
          >
            {submitting ? (
              <Loader2 size={18} className="animate-spin mx-auto" />
            ) : (
              t('MODIFY_RESERVATION_STRING_41')
            )}
          </button>
        </div>
      </div>
    );
  }

  // step === 'confirm' — matches the rest of the My Account redesign
  // (2xl serif heading, single muted subtitle, on-system buttons).
  // Drops the dated "Are you sure?" giant red text and the duplicate
  // email-confirmation paragraph (rolled into the subtitle now that
  // STRING_27 reads "This can't be undone. You'll get a confirmation
  // email."). STRING_21 and STRING_26_EMAIL keys are deprecated; left
  // in locale files only to avoid breaking other references during
  // a careful sweep.
  return (
    <div className="w-full flex flex-col items-center">
      <h2 className="text-2xl font-display mb-3 text-center">
        {t('MODIFY_RESERVATION_STRING_26')}
      </h2>
      <p className="text-sm text-foreground/60 text-center mb-8 max-w-sm">
        {t('MODIFY_RESERVATION_STRING_27')}
      </p>

      {error && (
        <p className="text-destructive text-sm text-center mb-4" role="alert">
          {error}
        </p>
      )}

      <div className="w-full max-w-xs space-y-3">
        {/* Safe action — secondary outlined button */}
        <button
          type="button"
          className="w-full py-3 rounded-xl border border-border text-foreground font-medium text-sm hover:bg-foreground/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-card"
          onClick={onClose}
        >
          {t('MODIFY_RESERVATION_STRING_26_GO_BACK')}
        </button>
        {/* Destructive confirm — design system §10 danger variant.
            Matches the destructive register used by the drawer Cancel
            button but at full saturation (this IS the actual destructive
            action, not the entry point). */}
        <button
          type="button"
          className="w-full py-3 rounded-xl bg-destructive text-destructive-foreground font-medium text-sm hover:bg-destructive/90 transition-colors disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-destructive focus-visible:ring-offset-2 focus-visible:ring-offset-card"
          onClick={handleCancel}
          disabled={submitting}
        >
          {submitting ? (
            <Loader2 size={18} className="animate-spin mx-auto" />
          ) : (
            t('MODIFY_RESERVATION_STRING_25')
          )}
        </button>
      </div>
    </div>
  );
};

export default CancelReservationView;
